import React, {useEffect} from 'react';
import {endpoint} from '~/api';
import {ISelectItem, ISelectProps, SelectInput} from '~/components/Controls/Select';
import {TableSortDirection} from '~/components/Table';
import {IDataSourceAdapter, useDataSource} from '~/lib/dataSource';
import {sortByString} from '~/lib/sort';

interface ITagsProps extends Omit<ISelectProps<string>, 'items'> {
    refresh?: boolean;
}

const TAGS_CACHE_KEY = 'tags.cache.v1';
const TAGS_CACHE_TTL_MS = 10 * 60 * 1000;

type TagsCache = {
    timestamp: number;
    items: string[];
};

let tagsCache: TagsCache | null = null;

const normalizeTags = (items: unknown): string[] => (Array.isArray(items)
    ? items.filter((item): item is string => typeof item === 'string')
    : []);

const readTagsCache = (): TagsCache | null => {
    if (tagsCache && Date.now() - tagsCache.timestamp < TAGS_CACHE_TTL_MS) {
        return tagsCache;
    }
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        const raw = window.localStorage.getItem(TAGS_CACHE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as TagsCache;
        if (!parsed || typeof parsed.timestamp !== 'number') {
            return null;
        }
        if (Date.now() - parsed.timestamp >= TAGS_CACHE_TTL_MS) {
            return null;
        }
        const items = normalizeTags(parsed.items);
        const cache = {timestamp: parsed.timestamp, items};
        tagsCache = cache;
        return cache;
    } catch {
        return null;
    }
};

const writeTagsCache = (items: string[]) => {
    const cache: TagsCache = {timestamp: Date.now(), items};
    tagsCache = cache;
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(TAGS_CACHE_KEY, JSON.stringify(cache));
    } catch {
    }
};

export const fetchTags = (options?: {force?: boolean}) => {
    if (!options?.force) {
        const cached = readTagsCache();
        if (cached) {
            return Promise.resolve(cached.items);
        }
    }
    return endpoint.get<string[]>('/tag')
        .then(r => normalizeTags(r.data))
        .then(items => {
            writeTagsCache(items);
            return items;
        });
};

const tagsAdapter: IDataSourceAdapter<string, {force?: boolean}> = {
    fetchRecords: (options) => fetchTags(options)
};

export const SelectTags = ({refresh, disabled, ...props}: ITagsProps) => {
    const {state, fetchRecords} = useDataSource(tagsAdapter);

    useEffect(() => {
        fetchRecords(refresh ? {force: true} : undefined);
    }, [refresh]);

    const toListItems = (items: string[]) => {
        let records: ISelectItem<string>[];
        records = items.map(tag => ({
            label: tag,
            value: tag
        }));
        return records;
    };

    return <SelectInput {...props} disabled={disabled || state.isPending}
        rightIcon={state.isPending && <i className='fa fa-spinner fa-spin'/>}
        items={sortByString(toListItems(state.records), 'label', TableSortDirection.ASC)}
    />;
};
